import React, { useState, memo, useMemo, useCallback } from "react";
import _ from "lodash";
import { useI18n } from "Hooks";
import classNames from "classnames";
import { connect } from "react-redux";

import {
  Button,
  Dialog,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
} from "@toddle-edu/ds-web";

import {
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  getFilteredStandardSets,
  getStandardSetsQueryVariables,
} from "../../modules/Utils";

import { getStandardSetsFromCache } from "AppComponents/StandardsSelectionModal/modules/StandardsSelectionModalGraphqlHelpers";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";

import classes from "./Breadcrumbs.scss";

const TEST_ID_PREFIX = "appComponents-standardSelectionModal-breadcrumbs";

const BreadCrumbs = memo(props => {
  const {
    onGoToPreviousStep,
    curriculumProgramId,
    selectedStandardSetDataScope,
    learningCourseId,
    assessmentSubjects,
    selectedStandardSet,
    selectedGradeScale,
    onChangeStandardSet,
    hasSelectedStandards,
    academicYearId,
    assessmentId,
    canMapGradeScale,
    curriculumProgramType,
  } = props;

  const [dialogParams, setDialogParams] = useState({});
  const { t } = useI18n();

  const standardSetQueryVariables = useMemo(
    () =>
      getStandardSetsQueryVariables({
        curriculumProgramId,
        gradeScaleIds: [],
        selectedStandardSetDataScope,
        learningCourseId,
        assessmentSubjects,
        academicYearId,
        assessmentId,
        curriculumProgramType,
      }),
    [
      curriculumProgramId,
      selectedStandardSetDataScope,
      learningCourseId,
      assessmentSubjects,
      academicYearId,
      assessmentId,
      curriculumProgramType,
    ]
  );

  const standardSets = useMemo(
    () => getStandardSetsFromCache(standardSetQueryVariables),
    [standardSetQueryVariables]
  );

  const standardsEdge = useMemo(
    () =>
      curriculumProgramType === CURRICULUM_TYPE_PYP
        ? _.get(standardSets, "benchmarksWrapper.standardsEdge", [])
        : _.get(standardSets, "standardsWrapper.standardsEdge", []),
    [curriculumProgramType, standardSets]
  );

  const standardEdges = useMemo(
    () => getFilteredStandardSets({ edges: standardsEdge, canMapGradeScale }),
    [canMapGradeScale, standardsEdge]
  );

  const dropdownOptions = useMemo(
    () =>
      _.map(standardEdges, ({ id, title, gradeScales }) => {
        const gradeScaleOptions = _.map(gradeScales, gradeScale => ({
          key: `${id}-${gradeScale.id}`,
          label: _.get(gradeScale, "displayName"),
        }));

        return {
          key: id,
          label: title,
          options: canMapGradeScale ? gradeScaleOptions : undefined,
          isSubMenu: canMapGradeScale && !_.isEmpty(gradeScaleOptions),
        };
      }),
    [canMapGradeScale, standardEdges]
  );

  const handleChangeStandardSet = useCallback(
    ({ key }) => {
      const [standardSetId, gradeScaleId] = _.split(key, "-");

      // Check if the selected standard set and grade scale are the same as the current selection
      if (
        _.isEqual(standardSetId, _.get(selectedStandardSet, "id")) &&
        (canMapGradeScale
          ? _.isEqual(gradeScaleId, _.get(selectedGradeScale, "id"))
          : true)
      ) {
        return;
      }

      const standardSet = _.find(standardEdges, { id: standardSetId });
      const gradeScale = _.find(_.get(standardSet, "gradeScales", []), {
        id: gradeScaleId,
      });

      const shouldShowDialog = !_.isEqual(
        standardSetId,
        _.get(selectedStandardSet, "id")
      );

      if (canMapGradeScale && hasSelectedStandards && shouldShowDialog) {
        setDialogParams({
          standardSet,
          gradeScale,
          shouldResetSelectedStandards: true,
        });
      } else {
        // if the user is switching to the same standard set but different grade scale then do not show the dialog
        onChangeStandardSet({ standardSet, gradeScale });
      }
    },
    [
      canMapGradeScale,
      selectedStandardSet,
      selectedGradeScale,
      standardEdges,
      hasSelectedStandards,
      onChangeStandardSet,
    ]
  );

  const handleCloseDialog = useCallback(() => {
    setDialogParams({});
  }, []);

  const handleConfirmDialog = useCallback(() => {
    onChangeStandardSet(dialogParams);
    handleCloseDialog();
  }, [dialogParams, onChangeStandardSet, handleCloseDialog]);

  const selectedOptionLabel = useMemo(
    () =>
      _.get(
        _.find(dropdownOptions, { key: _.get(selectedStandardSet, "id") }),
        "label"
      ),
    [dropdownOptions, selectedStandardSet]
  );

  const isSearchEnabled = _.size(dropdownOptions) > 5;
  const showDialog = !_.isEmpty(dialogParams);

  // Early return if only one or no standard sets
  if (_.size(standardEdges) <= 1) return null;

  return (
    <>
      <div className={classes.container}>
        <Button
          testId={`${TEST_ID_PREFIX}-selectSet`}
          type="inline"
          variant="neutral"
          size="small"
          className={classes.button}
          onClick={onGoToPreviousStep}
        >
          {t("academicSetup:standard_sets")}
        </Button>

        <div
          className={classNames("text-label-s", classes.interactiveSecondary)}
        >
          /
        </div>

        <Dropdown
          overlay={
            <DropdownMenu
              options={dropdownOptions}
              testId={TEST_ID_PREFIX}
              showSelection={true}
              onClick={handleChangeStandardSet}
              value={_.get(selectedStandardSet, "id")}
              isSearchEnabled={isSearchEnabled}
              placeholder={t("common:search_for_a_with_label", {
                label: t("common:set"),
              })}
            />
          }
        >
          <ButtonDisclosure
            testId={`${TEST_ID_PREFIX}-setName`}
            type="inline"
            variant="progressive"
            size="small"
            className={classes.button}
          >
            {selectedOptionLabel}
          </ButtonDisclosure>
        </Dropdown>
      </div>

      {showDialog && (
        <Dialog
          title={t("common:switch_standard_set")}
          message={t("common:switch_standard_set_msg")}
          buttonOneText={t("common:continue_editing")}
          buttonTwoText={t("common:discard_and_switch_set")}
          onButtonOneClick={handleCloseDialog}
          onButtonTwoClick={handleConfirmDialog}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
});

BreadCrumbs.displayName = "BreadCrumbs";

const mapStateToProps = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getSelectedAcademicYearId(state);

  return { curriculumProgramId, academicYearId };
};

export default connect(mapStateToProps, null)(BreadCrumbs);
